import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Settings, Check } from "lucide-react";
import { Link } from "react-router-dom";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  advertising: boolean;
}

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Toujours accepté
    analytics: false,
    advertising: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      
      // Initialiser Google Analytics si accepté
      if (savedPreferences.analytics) {
        initializeGoogleAnalytics();
      }
      
      // Initialiser Google Ads si accepté
      if (savedPreferences.advertising) {
        initializeGoogleAds();
      }
    }
  }, []);

  const initializeGoogleAnalytics = () => {
    // Initialiser Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const initializeGoogleAds = () => {
    // Initialiser Google Ads
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  };

  const acceptAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: true,
      advertising: true,
    };
    
    savePreferences(newPreferences);
    initializeGoogleAnalytics();
    initializeGoogleAds();
    setShowBanner(false);
  };

  const acceptNecessaryOnly = () => {
    const newPreferences = {
      necessary: true,
      analytics: false,
      advertising: false,
    };
    
    savePreferences(newPreferences);
    setShowBanner(false);
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
    
    if (preferences.analytics) {
      initializeGoogleAnalytics();
    }
    
    if (preferences.advertising) {
      initializeGoogleAds();
    }
    
    setShowBanner(false);
    setShowPreferences(false);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify(prefs));
    setPreferences(prefs);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Ne peut pas être désactivé
    
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4">
      <Card className="w-full max-w-2xl bg-background shadow-2xl border max-h-[90vh] overflow-y-auto">
        {!showPreferences ? (
          // Banner principal
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg sm:text-xl font-display font-bold text-foreground">
                🍪 Gestion des Cookies
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBanner(false)}
                className="shrink-0 ml-2"
                aria-label="Fermer la bannière de cookies"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-sm sm:text-base text-muted-foreground mb-6">
              Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic 
              et personnaliser le contenu. Vous pouvez choisir quels cookies accepter.
            </p>
            
            <div className="flex flex-col gap-3">
              <Button 
                onClick={acceptAll}
                className="w-full"
                size="sm"
              >
                <Check className="w-4 h-4 mr-2" />
                Accepter tous les cookies
              </Button>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowPreferences(true)}
                  className="flex-1"
                  size="sm"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Gérer les préférences
                </Button>
                
                <Button 
                  variant="secondary"
                  onClick={acceptNecessaryOnly}
                  className="flex-1"
                  size="sm"
                >
                  Nécessaires uniquement
                </Button>
              </div>
            </div>
            
            <div className="mt-4 text-xs text-muted-foreground">
              En continuant à naviguer, vous acceptez notre{" "}
              <Link to="/cookie-policy" className="text-primary hover:underline">
                Politique des Cookies
              </Link>{" "}
              et notre{" "}
              <Link to="/privacy-policy" className="text-primary hover:underline">
                Politique de Confidentialité
              </Link>
              .
            </div>
          </div>
        ) : (
          // Préférences détaillées
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-lg sm:text-xl font-display font-bold text-foreground">
                Préférences des Cookies
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreferences(false)}
                className="shrink-0 ml-2"
                aria-label="Fermer les préférences de cookies"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4 sm:space-y-6">
              {/* Cookies nécessaires */}
              <div className="flex items-start justify-between p-3 sm:p-4 border rounded-lg bg-muted/30">
                <div className="flex-1 pr-3">
                  <h4 className="font-semibold text-foreground mb-2 text-sm sm:text-base">
                    Cookies Strictement Nécessaires
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Ces cookies sont essentiels au fonctionnement du site et ne peuvent pas être désactivés.
                  </p>
                </div>
                <div className="shrink-0">
                  <div className="w-10 h-5 sm:w-12 sm:h-6 bg-primary rounded-full flex items-center justify-end pr-1">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-background rounded-full"></div>
                  </div>
                </div>
              </div>
              
              {/* Cookies analytiques */}
              <div className="flex items-start justify-between p-3 sm:p-4 border rounded-lg">
                <div className="flex-1 pr-3">
                  <h4 className="font-semibold text-foreground mb-2 text-sm sm:text-base">
                    Cookies Analytiques
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Ces cookies nous aident à comprendre comment vous utilisez notre site (Google Analytics).
                  </p>
                </div>
                <div className="shrink-0">
                  <button
                    onClick={() => togglePreference('analytics')}
                    className={`w-10 h-5 sm:w-12 sm:h-6 rounded-full flex items-center transition-colors ${
                      preferences.analytics ? 'bg-primary justify-end pr-1' : 'bg-muted justify-start pl-1'
                    }`}
                  >
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-background rounded-full"></div>
                  </button>
                </div>
              </div>
              
              {/* Cookies publicitaires */}
              <div className="flex items-start justify-between p-3 sm:p-4 border rounded-lg">
                <div className="flex-1 pr-3">
                  <h4 className="font-semibold text-foreground mb-2 text-sm sm:text-base">
                    Cookies Publicitaires
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Ces cookies permettent de vous proposer des publicités personnalisées (Google Ads).
                  </p>
                </div>
                <div className="shrink-0">
                  <button
                    onClick={() => togglePreference('advertising')}
                    className={`w-10 h-5 sm:w-12 sm:h-6 rounded-full flex items-center transition-colors ${
                      preferences.advertising ? 'bg-primary justify-end pr-1' : 'bg-muted justify-start pl-1'
                    }`}
                  >
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-background rounded-full"></div>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button 
                onClick={saveCustomPreferences}
                className="flex-1"
                size="sm"
              >
                Enregistrer les préférences
              </Button>
              
              <Button 
                variant="outline"
                onClick={acceptAll}
                className="flex-1"
                size="sm"
              >
                Accepter tout
              </Button>
            </div>
            
            <div className="mt-4 text-xs text-muted-foreground">
              Pour plus d'informations, consultez notre{" "}
              <Link to="/cookie-policy" className="text-primary hover:underline">
                Politique des Cookies
              </Link>
              .
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

// Déclaration globale pour TypeScript
declare global {
  function gtag(...args: any[]): void;
}